import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalStyles, useTheme } from '@mui/material';
import { useTourIntentContext } from '../context/TourIntentContext';
import { fetchUserTours } from '../services/backendAPI';
import { tourManager } from './tourManager';
import { tourConfig, isTourAvailable } from './tourConfig';
import { isAnyModalOpen, isGuestUser } from './tourHelpers';
import { getTourGlobalStyles } from './tourStyles';
import WhatsNewModal from './WhatsNewModal';

// Highest-priority tour the backend enabled, this build has, and the user hasn't closed this session
const getNextTourId = (tours) =>
  Object.entries(tours || {})
    .filter(([id, enabled]) =>
      String(enabled).toLowerCase() === 'true' && isTourAvailable(id) && !tourManager.closedTourIds.has(id)
    )
    .map(([id]) => id)
    .sort((a, b) => (tourConfig[b].priority ?? 0) - (tourConfig[a].priority ?? 0))[0];

const TourProvider = ({ showCFDModal }) => {
  const isProfileLoaded = useSelector((state) => state.profile.isLoaded);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { broadcastIntent, clearIntent } = useTourIntentContext();
  const theme = useTheme();
  const tourGlobalStyles = useMemo(() => getTourGlobalStyles(theme), [theme]);
  const [tours, setTours] = useState(null);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [modalTourId, setModalTourId] = useState(null);

  // GET /api/v1/user/tours is the only source of truth. Waiting for the profile makes the
  // guest check reliable (it sets user_type); guests never get tours.
  useEffect(() => {
    if (!isProfileLoaded || isGuestUser()) return;
    fetchUserTours()
      .then((response) => setTours(response?.data?.tours ?? null))
      .catch((error) => console.error('TourProvider: Failed to fetch tours', error));
  }, [isProfileLoaded]);

  useEffect(() => {
    if (isTourRunning || !getNextTourId(tours)) return;

    // Wait 2s so redirects (e.g. first login) settle, then keep waiting while a dialog such as
    // the order window or CFD notice is open. Re-check the tour on the tick: it may be done by then.
    const timer = setInterval(() => {
      if (isAnyModalOpen()) return;
      clearInterval(timer);
      const nextTourId = getNextTourId(tours);
      if (nextTourId) setModalTourId(nextTourId);
    }, 2000);
    return () => clearInterval(timer);
  }, [tours, isTourRunning]);

  useEffect(() => {
    tourManager.handleRouteChange(pathname);
  }, [pathname]);

  useEffect(() => () => tourManager.stop(), []);

  const handleSkipModal = () => {
    tourManager.markCompleted(modalTourId);
    setModalTourId(null);
  };

  const handleStartTour = (tourId) => {
    setModalTourId(null);
    const started = tourManager.start(tourId, {
      navigate,
      broadcastIntent,
      onStop: () => {
        setIsTourRunning(false);
        clearIntent(); // drop an intent no page handled, so it can't fire later
      },
    });
    // Both updates commit when this click handler ends, before the first popover draws
    if (started) setIsTourRunning(true);
  };

  return (
    <>
      {isTourRunning && <GlobalStyles styles={tourGlobalStyles} />}
      <WhatsNewModal
        // The CFD notice is decided after master settings load, so it can open after this
        // dialog was scheduled. Keep this hidden until it closes.
        open={!!modalTourId && !showCFDModal}
        tourId={modalTourId}
        onSkip={handleSkipModal}
        onStartTour={handleStartTour}
      />
    </>
  );
};

export default TourProvider;
